import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { useSlotIds } from '../../../hooks/useSlotIds';
import { useFetchOnce } from '../../../hooks';
import {
    ActionGetProgramSlotById,
    ActionGetTreatmentSlotById,
    ActionUpdateTreatmentSlot
} from '../../../store/Actions/TreatmentSlotAction';
import {
    ActionGetSolutionSlotById,
    ActionUpdateSolutionSlot
} from '../../../store/Actions/SolutionSlotAction';
import {
    ActionGetServiceSlotById,
    ActionUpdateServiceSlot
} from '../../../store/Actions/ServiceSlotAction';
import { ActionUpdateProgrammeSlot } from '../../../store/Actions/ServiceAction';
import { generateInitialValues } from '../formConfig';

export const useInpatientAssessment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);

    // Get patient detail from Redux
    const patientDetail = useSelector(state => state?.patient?.CcPaperDetail);

    const {
        activeSlotId,
        activeSlotName,
        programSlotId,
        serviceSlotId,
        treatmentSlotId,
        id,
        roomName,
        roomType
    } = useSlotIds();

    const getSlotAction = () => {
        if (programSlotId) return ActionGetProgramSlotById;
        if (treatmentSlotId) return ActionGetTreatmentSlotById;
        if (serviceSlotId) return ActionGetServiceSlotById;
        return ActionGetSolutionSlotById;
    };

    const { loading } = useFetchOnce({
        action: getSlotAction(),
        actionParams: { Query: `/${activeSlotId}` },
        dependency: activeSlotId,
        enabled: !!activeSlotId,
        onSuccess: (response) => {
            if (response?.data?.[0]?.assessmentPlan) {
                setFormData(response.data[0].assessmentPlan);
            }
        },
    });

    const getInitialValues = () => {
        const initialValues = generateInitialValues(patientDetail);
        if (formData) {
            const migratedData = { ...formData };
            // Migrate legacy BP keys to new combined BP field if they exist
            if (!migratedData.bp && (migratedData.bpSys || migratedData.bpDia)) {
                migratedData.bp = `${migratedData.bpSys || ''}/${migratedData.bpDia || ''}`;
            }
            // Migrate legacy respiratoryRate key to new rr key if it exists
            if (!migratedData.rr && migratedData.respiratoryRate) {
                migratedData.rr = migratedData.respiratoryRate;
            }
            return { ...initialValues, roomName, roomType, ...migratedData };
        }
        return { ...initialValues, roomName, roomType };
    };

    const handleSubmit = (values, { setSubmitting, setErrors, validateForm, setTouched }) => {
        validateForm().then(errors => {
            if (Object.keys(errors).length > 0) {
                const touchedFields = {};
                Object.keys(errors).forEach(field => {
                    touchedFields[field] = true;
                });
                setTouched(touchedFields);
                notification.error({
                    message: 'Validation Error',
                    description: 'Please fill all required fields!',
                });
                setSubmitting(false);
                return;
            }

            const sanitizedValues = { ...values };
            if (!sanitizedValues.addictions || sanitizedValues.addictions.length === 0) {
                sanitizedValues.smokingDuration = '';
                sanitizedValues.alcoholDuration = '';
            } else {
                if (!sanitizedValues.addictions.includes('smokingTobacco')) sanitizedValues.smokingDuration = '';
                if (!sanitizedValues.addictions.includes('alcohol')) sanitizedValues.alcoholDuration = '';
            }

            if (!sanitizedValues.medicalHistory || !sanitizedValues.medicalHistory.includes('diabetes')) {
                sanitizedValues.diabetesDuration = '';
            }

            const ActiveUpdateSlot = activeSlotName === 'service' ? ActionUpdateServiceSlot :
                activeSlotName === 'solution' ? ActionUpdateSolutionSlot :
                    activeSlotName === 'program' ? ActionUpdateProgrammeSlot :
                        ActionUpdateTreatmentSlot;

            dispatch(
                ActiveUpdateSlot(
                    {
                        Query: activeSlotId,
                        obj: { assessmentPlan: sanitizedValues },
                    },
                    (response) => {
                        if (response?.statusCode === 200) {
                            notification.success({
                                message: 'Success',
                                description: 'Assessment saved successfully!',
                            });
                            setSubmitting(false);
                            navigate(-1);
                        } else if (response?.statusCode === 400 && response?.errors) {
                            const backendErrors = response.errors;
                            setErrors(backendErrors);
                            const touchedFields = {};
                            Object.keys(backendErrors).forEach(field => {
                                touchedFields[field] = true;
                            });
                            setTouched(touchedFields);
                            notification.warning({
                                message: 'Validation Error',
                                description: 'Please fix the validation errors in the form.',
                            });
                            setSubmitting(false);
                        } else {
                            notification.error({
                                message: 'Error',
                                description: 'Failed to save assessment. Please try again.',
                            });
                            setSubmitting(false);
                        }
                    }
                )
            );
        });
    };

    const [isEditMode, setIsEditMode] = useState(false);

    const toggleEditMode = () => setIsEditMode(!isEditMode);

    return {
        loading,
        isEditMode,
        toggleEditMode,
        initialValues: getInitialValues(),
        handleSubmit,
        patientId: id,
        roomName,
        roomType,
        patientDetail
    };
};
